import { useModal } from '@ebay/nice-modal-react';
import { Box, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';

import { Button, LoadingButton, Typography, SizingMultiplier } from '@ocx/ui';
import { ModalDrawer } from '@ocx/ui-modals';
import { OfferDetailsMedia, OfferDetailsTermsAndDisclaimer, SurgeonWarning } from '@ocx/ui-offers';
import type { AltriaOffersNearbyStatus } from '@ocx/feat-local-offers';
import { BannerConfigurationProvider, type ExternalOfferFragment } from '@ocx/graphql';

import { messages } from './external-banner-details-popup.messages';
import { ExternalBannerChip, type ExternalBannerChipData } from '../external-banner/external-banner-chip.component';
import { offerMessages } from '../offer.messages';
import { ExternalBannerActions } from '../external-banner/use-get-external-banner-actions.hook';

export type ExternalBannerDetailsPopupControllerProps = {
  banner: ExternalOfferFragment;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
  actions?: ExternalBannerActions;
  chips?: ExternalBannerChipData[];
};

export const ExternalBannerDetailsPopupController = (props: ExternalBannerDetailsPopupControllerProps) => {
  const { visible, hide, remove } = useModal();
  const {
    banner: {
      provider,
      content: { title, titleMiddle, title2, imageUrl, surgeonGeneralWarning, terms },
    },
    hasAltriaOffersNearby,
    actions,
    chips = [],
  } = props;
  const shouldShowUnavailableNearbyWarning =
    provider === BannerConfigurationProvider.Altria && hasAltriaOffersNearby === 'not-available';
  const activateAction = actions?.ACTIVATE;
  const [isActivateInProgress, setIsActivateInProgress] = useState(() => activateAction?.inProgress ?? false);

  const onActivateClick = useCallback(async () => {
    if (!activateAction || isActivateInProgress) {
      return;
    }
    setIsActivateInProgress(true);
    try {
      const didActivate = await activateAction.onAction();
      if (didActivate) {
        hide();
      }
    } finally {
      setIsActivateInProgress(false);
    }
  }, [activateAction, hide, isActivateInProgress]);

  return (
    <ModalDrawer open={visible} onClose={hide} onExited={remove}>
      <Box
        sx={(theme) => ({
          borderTopLeftRadius: theme.spacing(2),
          borderTopRightRadius: theme.spacing(2),
          overflow: 'hidden',
        })}>
        {surgeonGeneralWarning && (
          <Box m={SizingMultiplier.md}>
            <SurgeonWarning message={surgeonGeneralWarning} size="large" square />
          </Box>
        )}
        {imageUrl && <OfferDetailsMedia variant="big-image" src={imageUrl} alt={title} />}
      </Box>
      <Stack spacing={SizingMultiplier.md} pt={SizingMultiplier.lg} px={SizingMultiplier.lg} pb={SizingMultiplier.xl}>
        <Typography variant="h6">
          {title} {titleMiddle} {title2}
        </Typography>
        {!!chips.length && (
          <Stack direction="row" flexWrap="wrap" gap={SizingMultiplier.sm}>
            {chips.map((chip, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ExternalBannerChip key={`${chip.label}-${index}`} {...chip} />
            ))}
          </Stack>
        )}
        {shouldShowUnavailableNearbyWarning && (
          <Box
            sx={(theme) => ({
              px: SizingMultiplier.md,
              py: SizingMultiplier.md,
              backgroundColor: theme.palette.action.selected,
              borderRadius: 1.5,
            })}>
            <Typography variant="body2" color="text.primary">
              <FormattedMessage {...messages['external-banner-details-popup:unavailable-nearby-stores']} />
            </Typography>
          </Box>
        )}
        {terms && <OfferDetailsTermsAndDisclaimer terms={terms} />}
        {activateAction && (
          <LoadingButton fullWidth loading={isActivateInProgress} onClick={onActivateClick}>
            <FormattedMessage {...offerMessages['offers:activateButtonTitle']} />
          </LoadingButton>
        )}
        <Button variant="text" fullWidth onClick={hide}>
          <FormattedMessage {...buttonTextMessages['button-text:close']} />
        </Button>
      </Stack>
    </ModalDrawer>
  );
};
